import { Radio, RadioGroup } from "@headlessui/react";

import { JSX } from "react";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import TariffCard from "@/components/cards/TariffCard";
import { BuySubFrom } from "@/components/modals/BuySubscriptionModal/types";

import { useGetSubscription } from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn";

import { MODAL_ROUTES } from "../router/ModalRoutes";

const BuySubscriptionPricesModal = (): JSX.Element => {
  const { t } = useTranslation();
  const [params] = useSearchParams();

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useFormContext<BuySubFrom>();

  const { closeModal, openModal } = useModalRouter();

  const price = watch("price");

  const subscriptionId = params.get("subscription_id");

  const subscription = subscriptionId && useGetSubscription(+subscriptionId);

  if (!subscription) return <></>;

  const onSubmit: SubmitHandler<BuySubFrom> = (data): void => {
    openModal([MODAL_ROUTES.BUY_SUBSCRIPTION, MODAL_ROUTES.BUY_SUBSCRIPTION_METHODS], {
      price_id: data.price?.id.toString(),
    });
  };

  return (
    <div className="flex flex-col items-center">
      <h3 className={"text-center text-3xl font-semibold"}>{t("Subscription.subscription-purchase")}</h3>
      <div className={"mt-6 space-y-4"}>
        <div className={"flex items-center"}>
          <div className={cn("h-10 max-lg:h-12", subscription.icon && "mr-2 rounded-lg bg-white p-0.5")}>
            {subscription.icon ? (
              <img src={subscription.icon} alt="Start" className={"size-9 object-contain max-lg:size-11"} />
            ) : null}
          </div>
          <h3 className={"text-lg font-semibold max-lg:text-2xl/7"}>{subscription.title}</h3>
        </div>
        <p className={"text-grey-1 max-lg:text-sm/5"}>{subscription.description}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name={"price"}
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <RadioGroup {...field} aria-label="price" className={"mt-4 grid grid-cols-2 flex-col gap-4"}>
                {({ value }) => (
                  <>
                    {subscription.prices.map((price, index) => (
                      <Radio key={price.id} value={price} as={"div"}>
                        <TariffCard
                          value={value}
                          id={price.id}
                          price={price.price}
                          cashback_percentage={price.cashback_percentage}
                          period={price.period}
                          style={{
                            background: index === 1 ? "linear-gradient(316deg, #000 3.34%, #FF4B00 142.49%), #000" : "",
                          }}
                          className={cn({ "outline-red-1": errors.price })}
                        />
                      </Radio>
                    ))}
                  </>
                )}
              </RadioGroup>
            )}
          />
          <div className={"mt-6"}>
            {price ? (
              <p className={"flex items-end justify-between text-orange-1"}>
                <span className={"text-lg font-medium"}>{t("Subscription.overall")}</span>
                <span className={"text-2xl font-semibold"}>
                  {t("General.som", { value: (price.price / 100).thousandSeparator() })}
                </span>
              </p>
            ) : null}
            <div className={"mt-4 flex justify-end gap-6 max-lg:grid max-lg:grid-cols-2 max-lg:gap-3"}>
              <Button
                color={"white"}
                aria-label={t("Actions.cancel")}
                onClick={closeModal}
                className={"w-fit min-w-[14.375rem] text-nowrap max-lg:min-w-full"}
              >
                {t("Actions.cancel")}
              </Button>
              <Button type={"submit"} className={"w-fit min-w-[14.375rem] text-nowrap max-lg:min-w-full"}>
                {t("Actions.buy")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BuySubscriptionPricesModal;
